import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { IonButton, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { logInOutline } from 'ionicons/icons';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SideNavPage } from './side-nav.types';
import { SideNavService } from './side-nav.service';
import { SideNavIconComponent } from './side-nav-icon.component';
import { isSideNavIconInverted } from './side-nav-icons.registry';

@Component({
  selector: 'app-side-nav',
  standalone: true,
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    TranslatePipe,
    IonButton,
    IonIcon,
    SideNavIconComponent,
  ],
})
export class SideNavComponent implements OnInit, OnDestroy {
  @ViewChild('panelRoot') panelRoot?: ElementRef<HTMLElement>;

  @Input() pages: SideNavPage[] = [];
  @Input() selectedIndex = 0;
  @Input() userAvatar = '';
  @Input() userDisplayName = '';
  @Input() isAnonymous = false;

  @Output() navigate = new EventEmitter<number>();
  @Output() profileClick = new EventEmitter<void>();
  @Output() loginClick = new EventEmitter<void>();

  isOpen$ = this.sideNav.isOpen$;
  enabled$ = this.sideNav.enabled$;
  isPersistent$ = this.sideNav.isPersistent$;
  isPersistent = false;
  enabled = true;
  panelOpen = false;
  panelClosing = false;
  panelDragging = false;
  panelDragX = 0;

  @HostBinding('class.side-nav-host--persistent')
  get hostPersistent(): boolean {
    return this.isPersistent && this.enabled;
  }

  private openSub?: Subscription;
  private enabledSub?: Subscription;
  private persistentSub?: Subscription;
  private closeTimer?: ReturnType<typeof setTimeout>;
  private openRaf?: number;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchTracking = false;
  private static readonly CLOSE_MS = 320;
  private static readonly SWIPE_CLOSE_PX = 72;
  private static readonly SWIPE_LOCK_PX = 8;

  constructor(public sideNav: SideNavService) {
    addIcons({ logInOutline });
    this.isPersistent = this.sideNav.isPersistent;
    this.enabled = this.sideNav.enabled;
    if (this.isPersistent) {
      this.panelOpen = true;
    }
  }

  ngOnInit(): void {
    this.enabledSub = this.sideNav.enabled$.subscribe((enabled) => {
      this.enabled = enabled;
    });

    this.persistentSub = this.sideNav.isPersistent$.subscribe((persistent) => {
      this.isPersistent = persistent;
      if (persistent) {
        this.cancelOpenAnimation();
        this.resetPanelDrag();
        this.panelClosing = false;
        this.panelOpen = true;
      }
    });

    this.openSub = this.sideNav.isOpen$.subscribe((open) => {
      if (this.sideNav.isPersistent) {
        return;
      }
      if (open) {
        this.schedulePanelOpen();
      } else if (this.panelOpen || this.panelClosing) {
        this.resetPanelDrag();
        this.panelClosing = true;
        this.panelOpen = false;
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
        }
        this.closeTimer = setTimeout(() => {
          this.panelClosing = false;
          this.closeTimer = undefined;
        }, SideNavComponent.CLOSE_MS);
      }
    });
  }

  ngOnDestroy(): void {
    this.enabledSub?.unsubscribe();
    this.persistentSub?.unsubscribe();
    this.openSub?.unsubscribe();
    this.cancelOpenAnimation();
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
  }

  get panelDragTransform(): string | null {
    if (!this.panelDragging || this.panelDragX >= 0) {
      return null;
    }
    return `translateX(${this.panelDragX}px)`;
  }

  isIconInverted(svgPath: string): boolean {
    return isSideNavIconInverted(svgPath);
  }

  onBackdropClick(): void {
    this.onCloseClick();
  }

  onCloseClick(): void {
    if (!this.isPersistent) {
      this.sideNav.close();
    }
  }

  onPanelTouchStart(event: TouchEvent): void {
    if (
      this.isPersistent ||
      !this.panelOpen ||
      this.panelClosing ||
      event.touches.length !== 1
    ) {
      return;
    }
    this.touchTracking = true;
    this.panelDragging = false;
    this.panelDragX = 0;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  onPanelTouchMove(event: TouchEvent): void {
    if (!this.touchTracking || this.isPersistent || event.touches.length !== 1) {
      return;
    }

    const deltaX = event.touches[0].clientX - this.touchStartX;
    const deltaY = event.touches[0].clientY - this.touchStartY;

    if (!this.panelDragging) {
      if (
        Math.abs(deltaX) < SideNavComponent.SWIPE_LOCK_PX &&
        Math.abs(deltaY) < SideNavComponent.SWIPE_LOCK_PX
      ) {
        return;
      }
      if (Math.abs(deltaY) > Math.abs(deltaX) || deltaX > 0) {
        this.touchTracking = false;
        return;
      }
      this.panelDragging = true;
    }

    this.panelDragX = Math.min(0, deltaX);
    if (this.panelDragging) {
      event.preventDefault();
    }
  }

  onPanelTouchEnd(): void {
    if (!this.touchTracking && !this.panelDragging) {
      return;
    }

    if (
      this.panelDragging &&
      this.panelDragX <= -SideNavComponent.SWIPE_CLOSE_PX
    ) {
      this.resetPanelDrag();
      this.sideNav.close();
    } else {
      this.resetPanelDrag();
    }

    this.touchTracking = false;
  }

  onNavigate(index: number): void {
    this.navigate.emit(index);
  }

  onProfileClick(): void {
    this.profileClick.emit();
  }

  onLoginClick(): void {
    this.loginClick.emit();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.sideNav.isOpen || this.isPersistent) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.sideNav.close();
        break;
      case 'Tab':
        this.handleFocusTrap(event);
        break;
    }
  }

  private schedulePanelOpen(): void {
    this.cancelOpenAnimation();
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
    this.resetPanelDrag();
    this.panelClosing = false;
    this.panelOpen = false;

    this.openRaf = requestAnimationFrame(() => {
      this.openRaf = requestAnimationFrame(() => {
        this.openRaf = undefined;
        this.panelOpen = true;
      });
    });
  }

  private cancelOpenAnimation(): void {
    if (this.openRaf !== undefined) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = undefined;
    }
  }

  private resetPanelDrag(): void {
    this.panelDragging = false;
    this.panelDragX = 0;
    this.touchTracking = false;
  }

  private handleFocusTrap(event: KeyboardEvent): void {
    const root = this.panelRoot?.nativeElement?.closest('.side-nav');
    if (!root) {
      return;
    }
    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href], input, select, textarea'
      )
    ).filter(
      (el) => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null
    );
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
